/*
* Copyright (c) 2025 Puratos. All rights reserved.
*/

import { IItem, IViewItem, IViewPromo } from '../models/item';
import { getStore } from '../../../config/redux.storage';
import { getGTMInteractionCrossSell } from '../../../selectors/analytics';
import { formatNumber, formatStringNumber } from '../utils/helpers';
import { GA4StockType } from '../types/stock';
import {GA4ComponentType, mapComponentTypeToPageType} from '../types/component';
import {getSubTotalPrice} from "../../../selectors/cart";
import {GetPackagingItem} from "../../../interfaces/ecom/schema";
import { GA4PageType } from '../types/page';

const BRAND_NAME = 'Puratos';

export const viewItemParser = (
   payload: any,
   currency: string,
): IViewItem => {
   const item = payload;
   const state = getStore().getState();
   const activeProductIndex = item.activeProductIndex ?? 0;
   const activePackaging = item.Packaging[activeProductIndex];
   const materialNumber = activePackaging?.MaterialNumber;
   const { promotionId, promotionName } = getPromotionDetails(item, activePackaging);
   const crossSellBlock = state ? getGTMInteractionCrossSell(state, materialNumber) : "";

   const itemDetails: IItem = {
       item_id: materialNumber?.toString(),
       item_name: item.product.title,
       item_brand: BRAND_NAME,
       promotion_id: promotionId,
       promotion_name: promotionName,
       item_list_name: GA4ComponentType.PDP_ADD_TO_CART,
       item_variant: getVariant(activePackaging),
       index: 1,
       quantity: item.product.Quantity || getItemQuantity(state, materialNumber) || 0,
       price: getItemPrice(activePackaging),
       currency: currency,
       discount: hasDiscount(activePackaging) ? calculateDiscount(activePackaging) : "",
       coupon: hasDiscount(activePackaging) ? activePackaging?.Promotions[0]?.PromoCode ?? '' : "",
       crosssell_block: crossSellBlock,
       stock_traffic_light: GA4StockType[activePackaging?.StockType ?? GA4StockType.NONE] ?? ""
   };

   return { items: [itemDetails] } as IViewItem;
};

export const selectItemParser = (
    payload: any,
    currency: string,
    componentType: GA4ComponentType
 ): IViewItem => {
    const item = payload;
    const state = getStore().getState();
    const activeProductIndex = item.activeProductIndex ?? 0;
    const activePackaging = item.Packagings[activeProductIndex];
    const itemName = activePackaging?.AEMProdName || item.name || item.AEMProdName;
    const materialNumber = activePackaging?.MaterialNumber;
    const { promotionId, promotionName } = getPromotionDetails(item, activePackaging);
    const crossSellBlock = state ? getGTMInteractionCrossSell(state, materialNumber) : "";

    const itemDetails: IItem = {
        item_id: materialNumber?.toString(),
        item_name: itemName,
        item_brand: BRAND_NAME,
        promotion_id: promotionId,
        promotion_name: promotionName,
        item_list_name: componentType,
        item_variant: getVariant(activePackaging),
        index: 1,
        quantity: item.Quantity || getItemQuantity(state, materialNumber) || 0,
        price: getItemPrice(activePackaging),
        currency: currency,
        discount: hasDiscount(activePackaging) ? calculateDiscount(activePackaging) : "",
        coupon: hasDiscount(activePackaging) ? activePackaging?.Promotions[0]?.PromoCode ?? '' : "",
        crosssell_block: crossSellBlock,
        stock_traffic_light: GA4StockType[activePackaging?.StockType ?? GA4StockType.NONE] ?? ""
    };
 
    return { items: [itemDetails] } as IViewItem;
 };

export const addToCartParser = (
   payload: GetPackagingItem,
   currency: string,
   componentType: GA4ComponentType,
   quantity: number
): IViewItem => {
   const item = payload;
   const state = getStore().getState();
   const subtotal = getSubTotalPrice(state);
   const crossSellBlock = state ? getGTMInteractionCrossSell(state, item.MaterialNumber) : "";

   const itemDetails: IItem = {
       item_id: item.MaterialNumber?.toString(),
       item_name: item.AEMProdName,
       item_brand: BRAND_NAME,
       promotion_id: item.Promotions[0]?.PromoCode || "",
       promotion_name: item.Promotions[0]?.ShortPromoText || "",
       item_list_name: mapComponentTypeToPageType(componentType),
       item_variant: `${item.SalesUnit} ${item.Packaging}${item.BaseUnit}`,
       index: 1,
       quantity: getItemQuantity(state, item.MaterialNumber) + quantity || quantity,
       price: getItemPrice(item),
       currency: currency,
       discount: hasDiscount(item) ? calculateDiscount(item) : "",
       coupon: hasDiscount(item) ? item?.Promotions[0]?.PromoCode ?? '' : "",
       crosssell_block: crossSellBlock,
       cart_interaction_section: componentType,
       stock_traffic_light: GA4StockType[item?.StockType ?? GA4StockType.NONE] ?? ""
   };

   return { currency, value: subtotal + item.TotalPriceUnitDouble * quantity ,items: [itemDetails] } as IViewItem;
};

export const viewPromotionParser = (
    payload: any,
    currency: string,
    quantity: number
 ): IViewPromo => {
    const item = payload;
    const state = getStore().getState();
    const crossSellBlock = state ? getGTMInteractionCrossSell(state, item.MaterialNumber) : "";

    const itemDetails: IItem = {
        item_id: item.MaterialNumber?.toString(),
        item_name: item.AEMProdName,
        item_brand: BRAND_NAME,
        promotion_id: item.Promotions[0]?.PromoCode || "",
        promotion_name: item.Promotions[0]?.ShortPromoText || "",
        item_list_name: GA4ComponentType.PDP_ADD_TO_CART,
        item_variant: `${item.SalesUnit} ${item.Packaging}${item.BaseUnit}`,
        index: 1,
        quantity: getItemQuantity(state, item.MaterialNumber) + quantity || quantity,
        price: getItemPrice(item),
        currency: currency,
        discount: hasDiscount(item) ? calculateDiscount(item) : "",
        coupon: hasDiscount(item) ? item?.Promotions[0]?.PromoCode ?? '' : "",
        crosssell_block: crossSellBlock,
        stock_traffic_light: GA4StockType[item?.StockType ?? GA4StockType.NONE] ?? ""
    };
 
    return {promotion_id: itemDetails.promotion_id, promotion_name: itemDetails.promotion_name, items: [itemDetails] } as IViewPromo;
 };

 export const requestPriceParser = (
    payload: any,
    componentType: GA4ComponentType
 ): IViewPromo => {
    const item = payload;
    const activePackaging = item.Packagings[0];
    const materialNumber = activePackaging?.MaterialNumber || item.MaterialNumber;
    const itemName = activePackaging?.AEMProdName || item.title;

    const itemDetails: IItem = {
        item_id: materialNumber?.toString(),
        item_name: itemName,
        item_brand: BRAND_NAME,
        item_list_name: componentType,
        item_variant: getVariant(activePackaging),
        stock_traffic_light: GA4StockType[activePackaging?.StockType ?? GA4StockType.NONE] ?? ""
    };
 
    return { items: [itemDetails] } as IViewPromo;
 };



/* Helpers */
const getPromotionDetails = (item: any, activePackaging: any) => {
   if (item?.product?.promotion) {
       return {
           promotionId: item.product.promotion.PromoCode,
           promotionName: item.product.promotion.PromotionText
       };
   } else if (activePackaging?.Promotions?.[0]) {
       return {
           promotionId: activePackaging.Promotions[0].PromoCode,
           promotionName: activePackaging.Promotions[0].ShortPromoText
       };
   } else {
       return { promotionId: '', promotionName: '' };
   }
};

const getVariant = (activePackaging: any): string => {
   return activePackaging ? (activePackaging.SalesUnit
       ? `${activePackaging.SalesUnit} ${formatStringNumber(activePackaging.Packaging)} ${activePackaging.BaseUnit}`
       : activePackaging.Packaging) : "";
};

const getItemPrice = (item: any): number =>
    item
       ? formatNumber(item.TotalPriceUnitDouble || item.PriceUnitDouble || item.PriceSalesUnitDouble || undefined)
       : undefined;

const hasDiscount = (item: any): boolean =>
   Boolean(item?.Promotions?.[0] && item?.PromotionUnitPriceDouble);

const calculateDiscount = (item: any): number | undefined =>
   item
       ? formatNumber(item.TotalPriceUnitDouble - item.PromotionTotalPriceDouble || item.PriceUnitDouble - item.PromotionUnitPriceDouble)
       : undefined;

const getItemQuantity = (state: any, materialNumber: string): number => {
    return state?.myPuratos_cart?.data?.basketItems?.find(item => item.materialNumber.toString() === materialNumber.toString())?.quantity || 0;
}
